/**
 * Event Sync Module - 事件同步邏輯（首次/向前/增量同步）
 */

import { supabase } from '../core/supabaseClient'
import { eventBus } from '@shared/stores/globalState'
import { eventCacheService } from './cache'
import type {
  EventRow,
  SyncProgressCallback,
  SyncInfo,
  SyncRange,
  SyncCacheMeta,
  LoadEventsCallbacks,
  EventServiceContext
} from './types'

export function getUserFieldIds(ctx: EventServiceContext): string[] {
  if (!ctx.fieldStore) {
    return []
  }
  const fields = ctx.fieldStore.fields || ctx.fieldStore.dataField || []
  return fields.map((f: { id: string }) => f.id).filter(Boolean)
}

export function calculateSyncRange(meta: SyncCacheMeta): SyncRange {
  if (meta.syncProgress?.offset && meta.syncProgress.offset > 0) {
    return { needsFirstSync: true }
  }

  if (!meta.lastSyncTime) {
    return { needsFirstSync: true }
  }

  const daysSinceSync = (Date.now() - meta.lastSyncTime) / (1000 * 60 * 60 * 24)

  if (daysSinceSync > 30) {
    return { needsFirstSync: true }
  }

  if (daysSinceSync < 5 / (24 * 60)) {
    return { needsFirstSync: false, daysMissing: 0, syncFrom: new Date(meta.lastSyncTime) }
  }

  if (meta.oldestEventDate && meta.eventCount > 0) {
    const oldestAge = (Date.now() - meta.oldestEventDate) / (1000 * 60 * 60 * 24)
    if (oldestAge < 28) {
      return {
        needsFirstSync: false,
        needsBackwardSync: true,
        syncFrom: new Date(meta.lastSyncTime),
        backwardFrom: new Date(meta.oldestEventDate)
      }
    }
  }

  return {
    needsFirstSync: false,
    daysMissing: Math.ceil(daysSinceSync),
    syncFrom: new Date(meta.lastSyncTime)
  }
}

export async function firstSync(
  ctx: EventServiceContext,
  onProgress: SyncProgressCallback | undefined
): Promise<void> {
  const FIRST_SYNC_LIMIT = 500
  const FIRST_SYNC_MIN_DURATION = 10
  const maxRetries = 3

  const thirtyDaysAgo = new Date()
  thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30)
  const now = new Date()

  const fieldIds = getUserFieldIds(ctx)

  ctx.eventCache.clear()
  await eventCacheService.clearAll()

  let events: EventRow[] | null = null
  let retryCount = 0

  while (retryCount < maxRetries) {
    try {
      let query = ctx.query()
        .lastDays(30)
        .useDB()
        .minDuration(FIRST_SYNC_MIN_DURATION)
        .limit(FIRST_SYNC_LIMIT)

      if (fieldIds && fieldIds.length > 0) {
        query = query.fields(fieldIds)
      }

      events = await query.execute()
      break
    } catch (fetchError) {
      retryCount++
      const err = fetchError as Error
      console.warn(`[EventService] 首次載入失敗 (重試 ${retryCount}/${maxRetries}):`, err.message)
      if (retryCount < maxRetries) {
        await new Promise(r => setTimeout(r, 1000 * retryCount))
      }
    }
  }

  if (!events) {
    console.error('[EventService] 首次同步失敗，已達最大重試次數')
    return
  }

  for (const event of events) {
    ctx.eventCache.set(event.id, event)
  }

  if (events.length > 0) {
    await eventCacheService.saveEvents(events)
  }

  const syncInfo: SyncInfo = {
    syncType: 'first',
    startDate: thirtyDaysAgo,
    endDate: now,
    minDuration: FIRST_SYNC_MIN_DURATION
  }
  onProgress?.(events.length, events.length, events.length, 0, syncInfo)
}

export async function backwardSync(
  ctx: EventServiceContext,
  _range: SyncRange,
  onProgress: SyncProgressCallback | undefined
): Promise<void> {
  const batchSize = 200
  const thirtyDaysAgo = new Date()
  thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30)
  const now = new Date()

  const fieldIds = getUserFieldIds(ctx)

  let loadedCount = 0
  let newCount = 0
  let offset = 0
  let hasMore = true

  while (hasMore) {
    let query = ctx.query()
      .lastDays(30)
      .useDB()
      .minDuration(0)
      .limit(batchSize)
      .offset(offset)

    if (fieldIds && fieldIds.length > 0) {
      query = query.fields(fieldIds)
    }

    let batch: EventRow[] | null = null
    try {
      batch = await query.execute()
    } catch (error) {
      console.error('[EventService] 向前補齊失敗:', error)
      break
    }

    if (!batch || batch.length === 0) {
      hasMore = false
      break
    }

    for (const event of batch) {
      if (!ctx.eventCache.has(event.id)) {
        newCount++
      }
      ctx.eventCache.set(event.id, event)
    }
    await eventCacheService.saveEvents(batch)

    loadedCount += batch.length
    offset += batch.length

    const syncInfo: SyncInfo = {
      syncType: 'backward',
      startDate: thirtyDaysAgo,
      endDate: now
    }
    onProgress?.(loadedCount, loadedCount, newCount, 0, syncInfo)

    if (batch.length < batchSize) {
      hasMore = false
    }

    if (hasMore) {
      await new Promise(r => setTimeout(r, 500))
    }
  }
}

export async function incrementalSync(
  ctx: EventServiceContext,
  range: SyncRange,
  onProgress: SyncProgressCallback | undefined
): Promise<void> {
  const batchSize = 200
  const thirtyDaysAgo = new Date()
  thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30)
  const now = new Date()

  const fieldIds = getUserFieldIds(ctx)

  let loadedCount = 0
  let newCount = 0
  let updatedCount = 0
  let offset = 0
  let hasMore = true

  while (hasMore) {
    let query = supabase
      .from('events')
      .select('*')
      .gte('created_at', thirtyDaysAgo.toISOString())
      .or(`created_at.gte.${range.syncFrom!.toISOString()},updated_at.gte.${range.syncFrom!.toISOString()}`)
      .order('created_at', { ascending: false })
      .range(offset, offset + batchSize - 1)

    if (fieldIds && fieldIds.length > 0) {
      query = query.in('field_id', fieldIds)
    }

    let batch: EventRow[] | null = null
    try {
      const { data, error } = await query
      if (error) throw error
      batch = data as EventRow[]
    } catch (error) {
      console.error('[EventService] 增量同步失敗:', error)
      break
    }

    if (!batch || batch.length === 0) {
      hasMore = false
      break
    }

    for (const event of batch) {
      if (ctx.eventCache.has(event.id)) {
        updatedCount++
      } else {
        newCount++
      }
      ctx.eventCache.set(event.id, event)
    }
    await eventCacheService.saveEvents(batch)

    loadedCount += batch.length
    offset += batch.length

    const syncInfo: SyncInfo = { syncType: 'incremental', startDate: range.syncFrom!, endDate: now }
    onProgress?.(loadedCount, loadedCount, newCount, updatedCount, syncInfo)

    if (batch.length < batchSize) {
      hasMore = false
    }

    if (hasMore) {
      await new Promise(r => setTimeout(r, 500))
    }
  }
}

export async function loadEventsWithCache(
  ctx: EventServiceContext,
  callbacks: LoadEventsCallbacks = {}
): Promise<void> {
  const { onCacheLoaded, onProgress, onComplete, onError } = callbacks

  const emitProgress: SyncProgressCallback = (loaded, total, newCount, updatedCount, syncInfo) => {
    onProgress?.(loaded, total, newCount, updatedCount, syncInfo)
    eventBus.emit('eventService:syncProgress', { loaded, total, newCount, updatedCount, syncInfo })
  }

  try {
    await eventCacheService.init()

    const meta = await eventCacheService.getMeta() as unknown as SyncCacheMeta

    if (meta.eventCount > 0) {
      const cached = await eventCacheService.queryEvents({}, {})
      cached.forEach(e => ctx.eventCache.set(e.id, e as EventRow))
      console.log(`[EventService] 從緩存載入 ${cached.length} 個事件`)
      onCacheLoaded?.(cached.length)
      eventBus.emit('eventService:cacheLoaded', { count: cached.length })
    }

    const syncNeeded = calculateSyncRange(meta)

    let didFirstSync = false
    if (syncNeeded.needsFirstSync) {
      await firstSync(ctx, emitProgress)
      didFirstSync = true
    } else if (syncNeeded.needsBackwardSync) {
      await backwardSync(ctx, syncNeeded, emitProgress)
    } else if (syncNeeded.daysMissing && syncNeeded.daysMissing > 0) {
      await incrementalSync(ctx, syncNeeded, emitProgress)
    } else {
      emitProgress(0, 0, 0, 0)
    }

    await eventCacheService.clearExpired(30)

    const totalCount = ctx.eventCache.size
    onComplete?.(totalCount)
    eventBus.emit('eventService:syncComplete', { totalCount })

    if (didFirstSync) {
      setTimeout(() => {
        backwardSync(ctx, {} as SyncRange, emitProgress).then(() => {
          eventBus.emit('eventService:syncComplete', { totalCount: ctx.eventCache.size })
        }).catch(err => {
          console.error('[EventService] 背景補齊失敗:', err)
        })
      }, 100)
    }
  } catch (error) {
    console.error('[EventService] 載入失敗:', error)
    onError?.(error as Error)
    eventBus.emit('eventService:syncError', { error: (error as Error).message })
  }
}
